import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, RefreshCw, FileDown, Image, TrendingUp, Clock, 
  CheckCircle2, AlertCircle, MapPin, BarChart3, Droplets,
  MessageSquare, ThumbsUp, ThumbsDown, Minus, Package,
  Calendar, Filter, Users, ChevronDown, Trash2, Container, Printer
} from "lucide-react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { useTheme } from "../contexts/ThemeContext";
import ThemeToggle from "../components/ThemeToggle";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CHART_COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
  '#84cc16', '#e11d48', '#0ea5e9', '#a855f7'
];

const TYPE_LABELS = {
  'ressourceø': 'Ressourceøer',
  'ressourcespand': 'Ressourcespande', 
  'maxicontainer': 'Maxicontainere'
};

const TYPE_COLORS = {
  'ressourceø': '#3b82f6',
  'ressourcespand': '#22c55e',
  'maxicontainer': '#f59e0b'
};

// ===== CHART COMPONENT =====
function StatChartCard({ title, data, formatValue, defaultChartType = "bar", showTotal = true }) {
  const [chartType, setChartType] = useState(defaultChartType);
  const entries = Object.entries(data || {}).sort(([,a],[,b]) => (typeof b === 'object' ? b.value : b) - (typeof a === 'object' ? a.value : a));
  const values = entries.map(([,v]) => typeof v === 'object' ? v.value : v);
  const maxVal = Math.max(...values, 1);
  const total = values.reduce((a, b) => a + b, 0);
  const fmt = formatValue || ((v) => v);

  if (entries.length === 0) return (
    <div className="bg-card border border-border rounded-lg p-4">
      <h3 className="text-sm font-medium mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm">Ingen data</p>
    </div>
  );

  return (
    <div className="bg-card border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium">{title}</h3>
        <div className="flex gap-1">
          {['bar', 'bar_vertical', 'pie', 'line'].map(t => (
            <button key={t} onClick={() => setChartType(t)}
              className={`px-1.5 py-0.5 rounded text-[10px] ${chartType === t ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              data-testid={`chart-type-${t}`}>
              {t === 'bar' ? '━' : t === 'bar_vertical' ? '▮' : t === 'pie' ? '◑' : '⌇'}
            </button>
          ))}
        </div>
      </div>

      {chartType === 'bar' && (
        <div className="space-y-2">
          {entries.map(([name, val], i) => {
            const v = typeof val === 'object' ? val.value : val;
            return (
              <div key={name}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="truncate max-w-[60%]" title={name}>{name}</span>
                  <span className="font-bold">{fmt(v)}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${(v/maxVal)*100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {chartType === 'bar_vertical' && (
        <div className="flex items-end gap-1 h-40 mt-2">
          {entries.slice(0, 15).map(([name, val], i) => {
            const v = typeof val === 'object' ? val.value : val;
            return (
              <div key={name} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[9px] font-bold">{fmt(v)}</span>
                <div className="w-full rounded-t transition-all" style={{ height: `${(v/maxVal)*100}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length], minHeight: '2px' }} />
                <span className="text-[8px] text-muted-foreground truncate w-full text-center" title={name}>{name.substring(0,6)}</span>
              </div>
            );
          })}
        </div>
      )}

      {chartType === 'pie' && (
        <div className="flex items-center gap-4">
          <svg viewBox="0 0 100 100" className="w-32 h-32 flex-shrink-0">
            {entries.reduce((acc, [name, val], i) => {
              const v = typeof val === 'object' ? val.value : val;
              const pct = (v / total) * 100;
              const startAngle = acc.offset;
              const endAngle = startAngle + (pct / 100) * 360;
              const largeArc = pct > 50 ? 1 : 0;
              const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
              const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
              const x2 = 50 + 40 * Math.cos((endAngle - 90) * Math.PI / 180);
              const y2 = 50 + 40 * Math.sin((endAngle - 90) * Math.PI / 180);
              acc.elements.push(
                <path key={name} d={`M50,50 L${x1},${y1} A40,40 0 ${largeArc},1 ${x2},${y2} Z`} fill={CHART_COLORS[i % CHART_COLORS.length]} />
              );
              acc.offset = endAngle;
              return acc;
            }, { elements: [], offset: 0 }).elements}
          </svg>
          <div className="space-y-1 text-xs flex-1">
            {entries.map(([name, val], i) => {
              const v = typeof val === 'object' ? val.value : val;
              return (
                <div key={name} className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="truncate">{name}</span>
                  <span className="font-bold ml-auto">{fmt(v)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {chartType === 'line' && (
        <svg viewBox="0 0 300 120" className="w-full h-32 mt-2">
          <polyline fill="none" stroke={CHART_COLORS[0]} strokeWidth="2"
            points={entries.map(([,val], i) => {
              const v = typeof val === 'object' ? val.value : val;
              return `${(i / Math.max(entries.length - 1, 1)) * 280 + 10},${110 - (v / maxVal) * 100}`;
            }).join(' ')} />
          {entries.map(([name, val], i) => {
            const v = typeof val === 'object' ? val.value : val;
            const x = (i / Math.max(entries.length - 1, 1)) * 280 + 10;
            const y = 110 - (v / maxVal) * 100;
            return <circle key={name} cx={x} cy={y} r="3" fill={CHART_COLORS[0]}><title>{name}: {fmt(v)}</title></circle>;
          })}
        </svg>
      )}

      {showTotal && <div className="mt-2 pt-2 border-t border-border text-xs flex justify-between font-bold"><span>TOTAL</span><span>{fmt(total)}</span></div>}
    </div>
  );
}

export default function Analytics({ user }) {
  const navigate = useNavigate();
  const { eventId: routeEventId } = useParams();
  const token = localStorage.getItem("token");
  const analyticsRef = useRef(null);
  const { printMode } = useTheme();
  // Gæster har kun "kig-adgang" — skjul alle eksport/print/nulstil-knapper
  const isGuest = user?.role === 'gæst';
  
  // Hent eventId fra route params eller localStorage
  const eventId = routeEventId || localStorage.getItem('selectedEventId');
  
  // Active view (tabs)
  const [activeView, setActiveView] = useState('overblik');
  
  // Data
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [extendedStats, setExtendedStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Planning
  const [planningData, setPlanningData] = useState(null);
  const [binPlanningData, setBinPlanningData] = useState(null);
  const [maxiPlanningData, setMaxiPlanningData] = useState(null);
  const [loadingPlanning, setLoadingPlanning] = useState(false);
  const [planningSubTab, setPlanningSubTab] = useState('ressourceøer');
  const [fractionFilter, setFractionFilter] = useState(null);
  const [br18Data, setBr18Data] = useState(null);
  
  // Global filters
  const [selectedTypes, setSelectedTypes] = useState(['ressourceø', 'ressourcespand', 'maxicontainer']);
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedUnits, setSelectedUnits] = useState([]);
  
  // Time filters
  const [timeInterval, setTimeInterval] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedHour, setSelectedHour] = useState(new Date().getHours());
  const [selectedWeek, setSelectedWeek] = useState(Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / 604800000));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Compare
  const [compareMode, setCompareMode] = useState(false);
  const [compareDate, setCompareDate] = useState(new Date(Date.now() - 86400000).toISOString().split('T')[0]);
  const [compareHour, setCompareHour] = useState(new Date().getHours());
  const [compareWeek, setCompareWeek] = useState(Math.max(1, Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / 604800000) - 1));
  const [compareYear, setCompareYear] = useState(new Date().getFullYear());

  const getTimeFilter = useCallback(() => {
    switch (timeInterval) {
      case 'hour': {
        const d = new Date(selectedDate); d.setHours(selectedHour, 0, 0, 0);
        const s = d.toISOString(); d.setHours(selectedHour + 1);
        return { start: s, end: d.toISOString() };
      }
      case 'day': {
        const s = new Date(selectedDate); s.setHours(0,0,0,0);
        const e = new Date(selectedDate); e.setHours(23,59,59,999);
        return { start: s.toISOString(), end: e.toISOString() };
      }
      case 'week': {
        const soy = new Date(selectedYear, 0, 1);
        const d2a = (selectedWeek - 1) * 7 - soy.getDay() + 1;
        const ws = new Date(selectedYear, 0, 1 + d2a); ws.setHours(0,0,0,0);
        const we = new Date(ws); we.setDate(we.getDate() + 6); we.setHours(23,59,59,999);
        return { start: ws.toISOString(), end: we.toISOString() };
      }
      default: return null;
    }
  }, [timeInterval, selectedDate, selectedHour, selectedWeek, selectedYear]);

  const getCompareFilter = useCallback(() => {
    switch (timeInterval) {
      case 'hour': {
        const d = new Date(compareDate); d.setHours(compareHour, 0, 0, 0);
        const s = d.toISOString(); d.setHours(compareHour + 1);
        return { start: s, end: d.toISOString() };
      }
      case 'day': {
        const s = new Date(compareDate); s.setHours(0,0,0,0);
        const e = new Date(compareDate); e.setHours(23,59,59,999);
        return { start: s.toISOString(), end: e.toISOString() };
      }
      case 'week': {
        const soy = new Date(compareYear, 0, 1);
        const d2a = (compareWeek - 1) * 7 - soy.getDay() + 1;
        const ws = new Date(compareYear, 0, 1 + d2a); ws.setHours(0,0,0,0);
        const we = new Date(ws); we.setDate(we.getDate() + 6); we.setHours(23,59,59,999);
        return { start: ws.toISOString(), end: we.toISOString() };
      }
      default: return null;
    }
  }, [timeInterval, compareDate, compareHour, compareWeek, compareYear]);

  // Filter requests by time + type + area
  const filterByGlobal = useCallback((items) => {
    return items.filter(req => {
      // Type filter
      const pointMeta = (extendedStats?.points_meta || []).find(p => p.name === req.point_name || p.id === req.point_id);
      const pt = pointMeta?.point_type || 'ressourceø';
      if (!selectedTypes.includes(pt)) return false;
      
      // Area filter
      if (selectedArea && pointMeta?.area_id !== selectedArea) return false;
      
      // Zone filter
      if (selectedZone && pointMeta?.work_zone_id !== selectedZone) return false;
      
      // Unit filter
      if (selectedUnits.length > 0 && !selectedUnits.includes(pointMeta?.id)) return false;
      
      return true;
    });
  }, [extendedStats, selectedTypes, selectedArea, selectedZone, selectedUnits]);

  const filteredRequests = requests.filter(req => {
    // Time filter
    if (timeInterval !== 'all') {
      const tf = getTimeFilter();
      if (tf) {
        const rd = new Date(req.created_at || req.requested_at);
        if (rd < new Date(tf.start) || rd > new Date(tf.end)) return false;
      }
    }
    return true;
  });

  const globalFiltered = filterByGlobal(filteredRequests);

  const compareFiltered = (compareMode && timeInterval !== 'all') ? filterByGlobal(requests.filter(req => {
    const cf = getCompareFilter();
    if (!cf) return false;
    const rd = new Date(req.created_at || req.requested_at);
    return rd >= new Date(cf.start) && rd <= new Date(cf.end);
  })) : [];

  // Computed stats from filtered data
  const computedStats = {
    total: globalFiltered.length,
    completed: globalFiltered.filter(r => r.status === 'completed' || r.status === 'grøn').length,
    in_progress: globalFiltered.filter(r => r.status === 'in_progress' || r.status === 'gul').length,
    pending: globalFiltered.filter(r => r.status === 'pending' || r.status === 'rød').length,
    qr: globalFiltered.filter(r => r.source === 'qr').length,
    manual: globalFiltered.filter(r => r.source !== 'qr').length,
  };

  const compareStats = compareMode ? {
    total: compareFiltered.length,
    completed: compareFiltered.filter(r => r.status === 'completed' || r.status === 'grøn').length,
  } : null;

  const calcChange = (cur, prev) => {
    if (!prev || prev === 0) return null;
    return Math.round(((cur - prev) / prev) * 100);
  };

  // Build a lookup map for point names → point_type
  const pointTypeMap = {};
  (extendedStats?.points_meta || []).forEach(p => { pointTypeMap[p.name] = p.point_type; });

  const perPointStats = globalFiltered.reduce((acc, req) => {
    const name = req.point_name || 'Ukendt';
    if (!acc[name]) acc[name] = { total: 0, completed: 0, pending: 0, liters: 0 };
    acc[name].total++;
    if (req.status === 'completed' || req.status === 'grøn') acc[name].completed++;
    if (req.status === 'pending' || req.status === 'rød') acc[name].pending++;
    acc[name].liters += req.liters || 0;
    return acc;
  }, {});

  const formatLiters = (l) => l >= 1000 ? `${(l/1000).toFixed(1)}k L` : `${l} L`;

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, requestsRes, extendedRes] = await Promise.all([
        axios.get(`${API}/analytics/stats`, { headers }),
        axios.get(`${API}/emptying-requests`, { headers }),
        eventId ? axios.get(`${API}/events/${eventId}/analytics/extended`, { headers }) : Promise.resolve({ data: null })
      ]);
      setStats(statsRes.data);
      setRequests(requestsRes.data);
      setExtendedStats(extendedRes.data);
    } catch (error) {
      console.error("Analytics error:", error);
      toast.error("Kunne ikke hente statistik");
    } finally {
      setLoading(false);
    }
  };

  const loadPlanningData = async () => {
    if (!eventId) return;
    setLoadingPlanning(true);
    try {
      const [pointsRes, maxiRes, mappingRes] = await Promise.all([
        axios.get(`${API}/resource-points`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/maxicontainers?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/fraction-mapping?event_id=${eventId}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      const allPoints = pointsRes.data.filter(p => p.event_id === eventId);
      const sLiters = mappingRes.data?.size_liters || {};

      // === Ressourceøer ===
      const resourcePoints = allPoints.filter(p => p.point_type === 'ressourceø');
      const allSizes = new Set();
      resourcePoints.forEach(p => (p.containers || []).forEach(c => { if (c.container_size) allSizes.add(c.container_size); }));
      const containerSizes = Array.from(allSizes).sort();
      const planningRows = [];
      const totals = { byFraction: {}, bySize: {} };
      resourcePoints.forEach(point => {
        const cByF = {};
        (point.containers || []).forEach(c => {
          const f = c.fraction_type || 'Ukendt', s = c.container_size || 'Ukendt', n = c.count || 1;
          if (!cByF[f]) cByF[f] = {};
          cByF[f][s] = (cByF[f][s] || 0) + n;
          totals.byFraction[f] = (totals.byFraction[f] || 0) + n;
          totals.bySize[s] = (totals.bySize[s] || 0) + n;
        });
        Object.entries(cByF).forEach(([f, sizes]) => planningRows.push({ pointName: point.name || `RO-${point.id.substring(0,4)}`, pointId: point.id, fraction: f, sizes }));
      });
      // Beregn skilte (unikke fraktion+størrelse kombinationer per punkt)
      const signsByFraction = {};
      planningRows.forEach(row => {
        // Hvert row er (pointId, fraction) — sizes er { "240L": 2, "660L": 1 }
        // Skilte = antal unikke størrelser per fraktion (uanset antal containere)
        const sizeCount = Object.keys(row.sizes).length;
        signsByFraction[row.fraction] = (signsByFraction[row.fraction] || 0) + sizeCount;
      });
      setPlanningData({ rows: planningRows.sort((a, b) => a.pointName.localeCompare(b.pointName, 'da', { numeric: true, sensitivity: 'base' }) || a.fraction.localeCompare(b.fraction, 'da', { numeric: true, sensitivity: 'base' })), containerSizes, totals, totalContainers: Object.values(totals.byFraction).reduce((a,b)=>a+b,0), pointCount: resourcePoints.length, signsByFraction });

      // === Ressourcespande === (samme opbygning som Ressourceøer)
      const binPoints = allPoints.filter(p => p.point_type === 'ressourcespand');
      const allBinSizes = new Set();
      binPoints.forEach(p => (p.bins || []).forEach(b => { if (b.container_size) allBinSizes.add(b.container_size); }));
      const binContainerSizes = Array.from(allBinSizes).sort();
      const binPlanningRows = [];
      const binTotals = { byFraction: {}, bySize: {} };
      binPoints.forEach(point => {
        const bByF = {};
        (point.bins || []).forEach(b => {
          const f = b.fraction_type || 'Ukendt', s = b.container_size || 'Ukendt', n = b.count || 1;
          if (!bByF[f]) bByF[f] = {};
          bByF[f][s] = (bByF[f][s] || 0) + n;
          binTotals.byFraction[f] = (binTotals.byFraction[f] || 0) + n;
          binTotals.bySize[s] = (binTotals.bySize[s] || 0) + n;
        });
        Object.entries(bByF).forEach(([f, sizes]) => binPlanningRows.push({ pointName: point.name || `RS-${point.id.substring(0,4)}`, pointId: point.id, fraction: f, sizes }));
      });
      // Beregn skilte (unikke fraktion+størrelse kombinationer per punkt)
      const binSignsByFraction = {};
      binPlanningRows.forEach(row => {
        const sizeCount = Object.keys(row.sizes).length;
        binSignsByFraction[row.fraction] = (binSignsByFraction[row.fraction] || 0) + sizeCount;
      });
      setBinPlanningData({
        rows: binPlanningRows.sort((a, b) => a.pointName.localeCompare(b.pointName, 'da', { numeric: true, sensitivity: 'base' }) || a.fraction.localeCompare(b.fraction, 'da', { numeric: true, sensitivity: 'base' })),
        containerSizes: binContainerSizes,
        totals: binTotals,
        totalBins: Object.values(binTotals.byFraction).reduce((a,b)=>a+b,0),
        pointCount: binPoints.length,
        signsByFraction: binSignsByFraction
      });

      // === Maxicontainere === (samme opbygning som Ressourceøer, container_type som "størrelse")
      const maxiData = maxiRes.data || [];
      const allMaxiTypes = new Set();
      maxiData.forEach(m => { if (m.container_type) allMaxiTypes.add(m.container_type); });
      const maxiContainerTypes = Array.from(allMaxiTypes).sort();
      const maxiPlanningRows = [];
      const maxiTotals = { byFraction: {}, byType: {} };
      // Aggreger pr. maxicontainer (pointId) + fraktion, sizes = { containerType: count }
      const maxiAggregated = {};
      maxiData.forEach(m => {
        const f = m.fraction || 'Ukendt', t = m.container_type || 'Ukendt';
        const key = `${m.id}|||${f}`;
        if (!maxiAggregated[key]) {
          maxiAggregated[key] = {
            pointName: m.name || `MC-${m.id.substring(0,4)}`,
            pointId: m.id,
            fraction: f,
            sizes: {}
          };
        }
        maxiAggregated[key].sizes[t] = (maxiAggregated[key].sizes[t] || 0) + 1;
        maxiTotals.byFraction[f] = (maxiTotals.byFraction[f] || 0) + 1;
        maxiTotals.byType[t] = (maxiTotals.byType[t] || 0) + 1;
      });
      Object.values(maxiAggregated).forEach(row => maxiPlanningRows.push(row));
      // Beregn skilte for maxi (unikke fraktion+type kombinationer per punkt)
      const maxiSignsByFraction = {};
      maxiPlanningRows.forEach(row => {
        const typeCount = Object.keys(row.sizes).length;
        maxiSignsByFraction[row.fraction] = (maxiSignsByFraction[row.fraction] || 0) + typeCount;
      });
      setMaxiPlanningData({
        rows: maxiPlanningRows.sort((a, b) => a.pointName.localeCompare(b.pointName, 'da', { numeric: true, sensitivity: 'base' }) || a.fraction.localeCompare(b.fraction, 'da', { numeric: true, sensitivity: 'base' })),
        containerTypes: maxiContainerTypes,
        totals: maxiTotals,
        totalMaxi: maxiData.length,
        signsByFraction: maxiSignsByFraction
      });

      // === BR18 / Kapacitetsoversigt ===
      const br18Cards = [];
      // Ressourceøer
      resourcePoints.forEach(point => {
        const lines = [];
        let totalLiters = 0;
        (point.containers || []).forEach(c => {
          const f = c.fraction_type || 'Ukendt';
          const s = c.container_size || 'Ukendt';
          const n = c.count || 1;
          const litersPerUnit = sLiters[s] || 0;
          const cap = n * litersPerUnit;
          totalLiters += cap;
          lines.push({ fraction: f, size: s, count: n, litersPerUnit, capacity: cap });
        });
        if (lines.length > 0) br18Cards.push({ name: point.name || `RO-${point.id.substring(0,4)}`, type: 'Ressourceø', id: point.id, lines, totalLiters, note: point.br18_note || '' });
      });
      // Ressourcespande
      binPoints.forEach(point => {
        const lines = [];
        let totalLiters = 0;
        (point.bins || []).forEach(b => {
          const f = b.fraction_type || 'Ukendt';
          const s = b.container_size || '';
          const n = b.count || 1;
          const litersPerUnit = sLiters[s] || 0;
          const cap = n * litersPerUnit;
          totalLiters += cap;
          lines.push({ fraction: f, size: s, count: n, litersPerUnit, capacity: cap });
        });
        if (lines.length > 0) br18Cards.push({ name: point.name || `RS-${point.id.substring(0,4)}`, type: 'Ressourcespand', id: point.id, lines, totalLiters, note: point.br18_note || '' });
      });
      // Maxicontainere
      maxiData.forEach(m => {
        const s = m.container_type || 'Ukendt';
        const litersPerUnit = sLiters[s] || 0;
        const lines = [{ fraction: m.fraction || 'Ukendt', size: s, count: 1, litersPerUnit, capacity: litersPerUnit }];
        br18Cards.push({ name: m.name || `MC-${m.id.substring(0,4)}`, type: 'Maxicontainer', id: m.id, lines, totalLiters: litersPerUnit, note: m.br18_note || '' });
      });
      br18Cards.sort((a, b) => a.type.localeCompare(b.type, 'da') || a.name.localeCompare(b.name, 'da'));
      setBr18Data({ cards: br18Cards, sizeLiters: sLiters });

    } catch (error) { toast.error("Kunne ikke hente planlægningsdata"); } finally { setLoadingPlanning(false); }
  };

  useEffect(() => { loadData(); }, []);

  const saveBr18Note = async (card, note) => {
    try {
      const endpoint = card.type === 'Maxicontainer'
        ? `${API}/maxicontainers/${card.id}`
        : `${API}/resource-points/${card.id}`;
      await axios.put(endpoint, { br18_note: note }, { headers: { Authorization: `Bearer ${token}` } });
      // Update local state
      setBr18Data(prev => {
        if (!prev) return prev;
        const updated = prev.cards.map(c => c.id === card.id ? { ...c, note } : c);
        return { ...prev, cards: updated };
      });
      toast.success("Notat gemt");
    } catch { toast.error("Kunne ikke gemme notat"); }
  };

  const exportBR18 = async () => {
    if (!br18Data?.cards?.length) return;
    const wb = XLSX.utils.book_new();
    const rows = [['Type', 'Navn', 'Fraktion', 'Antal', 'Størrelse', 'Liter/stk', 'Kapacitet (L)', 'Notat']];
    br18Data.cards.forEach(card => {
      card.lines.forEach((line, i) => {
        rows.push([i === 0 ? card.type : '', i === 0 ? card.name : '', line.fraction, line.count, line.size, line.litersPerUnit || '', line.capacity || '', i === 0 ? card.note : '']);
      });
      rows.push(['', '', '', '', '', 'Total:', card.totalLiters, '']);
      rows.push([]);
    });
    const totalAll = br18Data.cards.reduce((a, c) => a + c.totalLiters, 0);
    rows.push(['', '', '', '', '', 'SAMLET KAPACITET:', totalAll, '']);
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 8 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Kapacitetsoversigt');
    XLSX.writeFile(wb, `kapacitetsoversigt-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success("Kapacitetsoversigt Excel eksporteret!");
  };

  const exportCapacityPDF = async () => {
    if (!br18Data?.cards?.length) return;
    try {
      const typeMap = { ressourceøer: 'Ressourceø', ressourcespande: 'Ressourcespand', maxicontainere: 'Maxicontainer' };
      const typeLabel = typeMap[planningSubTab] || 'Alle';
      const filteredCards = (br18Data.cards || []).filter(c => !typeMap[planningSubTab] || c.type === typeMap[planningSubTab]);
      if (!filteredCards.length) { toast.error("Ingen kort at eksportere"); return; }

      const eventName = localStorage.getItem('selectedEventName') || '';
      const dateStr = new Date().toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const PAGE_W = 210, PAGE_H = 297;
      const MARGIN_X = 12, TOP_Y = 24, BOTTOM_Y = 283;
      const COL_GAP = 4, ROW_GAP = 4;
      const COLS = 3;
      const COL_W = (PAGE_W - 2 * MARGIN_X - (COLS - 1) * COL_GAP) / COLS;
      const CARD_INNER = COL_W - 6; // 3mm padding hver side

      const drawHeader = () => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(30, 30, 30);
        pdf.text(`Kapacitetsoversigt / BR18${eventName ? ' — ' + eventName : ''}`, MARGIN_X, 12);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(120, 120, 120);
        pdf.text(typeLabel, MARGIN_X, 18);
        pdf.setDrawColor(200, 200, 200);
        pdf.line(MARGIN_X, 20, PAGE_W - MARGIN_X, 20);
      };
      drawHeader();

      // Pre-beregn linjerne for ét kort — returnerer array af {type, text/fraction/mid/cap, lines}
      const prepareCardLines = (card) => {
        const items = [];
        card.lines.forEach(line => {
          const capText = line.capacity > 0 ? `${line.capacity.toLocaleString('da-DK')}L` : '-';
          // Hvis size-strengen er lang → to linjer: "fraction" øverst, "count x size" på næste
          const sizeFull = `${line.count}x${line.size}`;
          pdf.setFontSize(8);
          const fullWidth = pdf.getTextWidth(sizeFull);
          // Hvis fraction + "  " + mid + "  " + cap passer på én linje, brug enkeltlinje
          const fractionW = pdf.getTextWidth(String(line.fraction));
          const capW = pdf.getTextWidth(capText);
          const singleLineOK = fractionW + fullWidth + capW + 6 <= CARD_INNER; // 6mm spacing buffer
          if (singleLineOK) {
            items.push({ mode: 'single', fraction: String(line.fraction), mid: sizeFull, cap: capText });
          } else {
            // Split size over 2 linjer via splitTextToSize
            const sizeLines = pdf.splitTextToSize(sizeFull, CARD_INNER - capW - 4);
            items.push({ mode: 'multi', fraction: String(line.fraction), midLines: sizeLines, cap: capText });
          }
        });
        return items;
      };

      const estimateCardHeight = (card, preparedItems) => {
        pdf.setFontSize(10);
        // Navn kan wrappe
        const nameLines = pdf.splitTextToSize(card.name, CARD_INNER);
        const nameH = nameLines.length * 4.5 + 2;
        let linesH = 0;
        preparedItems.forEach(it => {
          linesH += it.mode === 'multi' ? (1 + it.midLines.length) * 3.8 + 1 : 5;
        });
        const totalBlock = 9;
        let noteH = 0;
        if (card.note) {
          pdf.setFontSize(7);
          const noteLines = pdf.splitTextToSize(card.note, CARD_INNER);
          noteH = noteLines.length * 3.5 + 1;
        }
        const padding = 6;
        return nameH + linesH + totalBlock + noteH + padding;
      };

      const drawCard = (card, preparedItems, colIdx, startY) => {
        const x = MARGIN_X + colIdx * (COL_W + COL_GAP);
        const h = estimateCardHeight(card, preparedItems);
        pdf.setDrawColor(215, 215, 215);
        pdf.setFillColor(252, 252, 252);
        pdf.roundedRect(x, startY, COL_W, h, 1.5, 1.5, 'FD');

        // Navn (kan wrappe)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(25, 25, 25);
        const nameLines = pdf.splitTextToSize(card.name, CARD_INNER);
        let cursor = startY + 5;
        nameLines.forEach(nl => {
          pdf.text(nl, x + 3, cursor);
          cursor += 4.5;
        });
        cursor += 2;

        // Linjer
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(55, 55, 55);
        preparedItems.forEach(it => {
          if (it.mode === 'single') {
            pdf.text(it.fraction, x + 3, cursor);
            pdf.text(it.mid, x + COL_W / 2, cursor, { align: 'center' });
            pdf.text(it.cap, x + COL_W - 3, cursor, { align: 'right' });
            cursor += 5;
          } else {
            pdf.text(it.fraction, x + 3, cursor);
            pdf.text(it.cap, x + COL_W - 3, cursor, { align: 'right' });
            cursor += 3.8;
            it.midLines.forEach(ml => {
              pdf.text(ml, x + 3, cursor);
              cursor += 3.8;
            });
            cursor += 1;
          }
        });

        // Divider + total
        pdf.setDrawColor(220, 220, 220);
        pdf.line(x + 3, cursor + 1, x + COL_W - 3, cursor + 1);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text('Total kapacitet', x + 3, cursor + 6);
        pdf.text(`${(card.totalLiters || 0).toLocaleString('da-DK')}L`, x + COL_W - 3, cursor + 6, { align: 'right' });
        cursor += 9;

        if (card.note) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(7);
          pdf.setTextColor(110, 110, 110);
          const noteLines = pdf.splitTextToSize(card.note, CARD_INNER);
          noteLines.forEach(nl => {
            pdf.text(nl, x + 3, cursor + 3);
            cursor += 3.5;
          });
        }
        return h;
      };

      const colYs = [TOP_Y, TOP_Y, TOP_Y];
      const newPage = () => {
        pdf.addPage();
        drawHeader();
        colYs[0] = colYs[1] = colYs[2] = TOP_Y;
      };

      filteredCards.forEach((card) => {
        const prepared = prepareCardLines(card);
        const h = estimateCardHeight(card, prepared);
        let chosenCol = 0;
        for (let i = 1; i < COLS; i++) if (colYs[i] < colYs[chosenCol]) chosenCol = i;
        if (colYs[chosenCol] + h > BOTTOM_Y) {
          newPage();
          chosenCol = 0;
        }
        drawCard(card, prepared, chosenCol, colYs[chosenCol]);
        colYs[chosenCol] += h + ROW_GAP;
      });

      // Footer på ALLE sider
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(130, 130, 130);
        pdf.setDrawColor(220, 220, 220);
        pdf.line(MARGIN_X, 289, PAGE_W - MARGIN_X, 289);
        pdf.text(`Udskrevet: ${dateStr}`, MARGIN_X, PAGE_H - 6);
        pdf.text(`Side ${i}/${pageCount}`, PAGE_W - MARGIN_X, PAGE_H - 6, { align: 'right' });
      }

      const safeEvent = (eventName || 'event').replace(/[^a-zæøåA-ZÆØÅ0-9_-]+/gi, '-').toLowerCase();
      pdf.save(`kapacitetsoversigt-${safeEvent}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("Kapacitetsoversigt PDF eksporteret!");
    } catch (err) {
      console.error(err);
      toast.error("Fejl ved PDF-generering");
    }
  };

  // Bestillingsliste til renovationsselskab: aggregeret pr. fraktion × containerstørrelse + skilte total
  const exportOrderListPDF = async () => {
    try {
      // Saml containerstørrelser + antal pr. fraktion på tværs af ressourceøer, ressourcespande og maxicontainere
      const perFraction = {}; // { fraction: { sizes: { '240L': count }, signs: n } }
      const ensure = (f) => { if (!perFraction[f]) perFraction[f] = { sizes: {}, signs: 0 }; return perFraction[f]; };

      // Ressourceøer
      (planningData?.rows || []).forEach(row => {
        const entry = ensure(row.fraction);
        Object.entries(row.sizes || {}).forEach(([s, n]) => { entry.sizes[s] = (entry.sizes[s] || 0) + n; });
      });
      Object.entries(planningData?.signsByFraction || {}).forEach(([f, n]) => { ensure(f).signs += n; });

      // Ressourcespande
      (binPlanningData?.rows || []).forEach(row => {
        const entry = ensure(row.fraction);
        Object.entries(row.sizes || {}).forEach(([s, n]) => { entry.sizes[s] = (entry.sizes[s] || 0) + n; });
      });
      Object.entries(binPlanningData?.signsByFraction || {}).forEach(([f, n]) => { ensure(f).signs += n; });

      // Maxicontainere (container_type behandles som størrelse)
      (maxiPlanningData?.rows || []).forEach(row => {
        const entry = ensure(row.fraction);
        Object.entries(row.sizes || {}).forEach(([s, n]) => { entry.sizes[s] = (entry.sizes[s] || 0) + n; });
      });
      Object.entries(maxiPlanningData?.signsByFraction || {}).forEach(([f, n]) => { ensure(f).signs += n; });

      const fractions = Object.keys(perFraction).sort((a, b) => a.localeCompare(b, 'da'));
      if (fractions.length === 0) { toast.error("Ingen data at eksportere"); return; }

      // Saml alle unikke størrelser (dedupliceret på tværs af typer)
      const allSizes = new Set();
      fractions.forEach(f => Object.keys(perFraction[f].sizes).forEach(s => allSizes.add(s)));
      const sizes = Array.from(allSizes).sort((a, b) => {
        // Sortér liter-værdier numerisk hvis muligt
        const na = parseInt(a.match(/\d+/)?.[0] || '0', 10);
        const nb = parseInt(b.match(/\d+/)?.[0] || '0', 10);
        if (na && nb && na !== nb) return na - nb;
        return a.localeCompare(b, 'da');
      });

      // Byg PDF
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape for bredere tabel
      const eventName = localStorage.getItem('selectedEventName') || '';
      const dateStr = new Date().toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });

      // Venstrestillet header (samme stil som Kapacitetsoversigt)
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.setTextColor(30, 30, 30);
      pdf.text('Bestillingsliste til renovationsselskab', 12, 12);
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(120, 120, 120);
      pdf.text(eventName || '', 12, 18);
      pdf.setDrawColor(200, 200, 200);
      pdf.line(12, 20, 285, 20);

      // Tom celle = INGEN markering (ingen ☐)
      const head = [['Fraktion', ...sizes, 'Antal skilte']];
      const body = fractions.map(f => {
        const row = perFraction[f];
        return [f, ...sizes.map(s => (row.sizes[s] ? String(row.sizes[s]) : '')), String(row.signs)];
      });
      const sizeTotals = sizes.map(s => fractions.reduce((a, f) => a + (perFraction[f].sizes[s] || 0), 0));
      const skilteTotal = fractions.reduce((a, f) => a + perFraction[f].signs, 0);
      const foot = [['TOTAL', ...sizeTotals.map(n => String(n)), String(skilteTotal)]];

      // Lodrette headers: vi tegner dem manuelt i didDrawCell hook
      const HEADER_H = 42; // plads til lodret tekst
      autoTable(pdf, {
        head, body, foot,
        startY: 24,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 2, halign: 'center', valign: 'middle', overflow: 'linebreak' },
        headStyles: { fillColor: [50, 50, 60], textColor: [255, 255, 255], fontStyle: 'bold', minCellHeight: HEADER_H, valign: 'bottom' },
        footStyles: { fillColor: [200, 220, 80], textColor: [0, 0, 0], fontStyle: 'bold' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold', cellWidth: 30 } },
        // Gør alle størrelse-kolonner smalle så det passer på A4-landscape
        didParseCell: (data) => {
          if (data.section === 'head' && data.column.index > 0 && data.column.index < data.table.columns.length - 1) {
            data.cell.text = []; // Ryd default tekst — vi tegner den rotateret senere
            data.cell.styles.cellWidth = Math.max(10, Math.min(18, (285 - 30 - 20) / sizes.length));
          }
          if (data.section === 'head' && data.column.index === data.table.columns.length - 1) {
            data.cell.styles.cellWidth = 20;
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'head' && data.column.index > 0 && data.column.index <= sizes.length) {
            const headerText = sizes[data.column.index - 1] || '';
            const cx = data.cell.x + data.cell.width / 2;
            const cy = data.cell.y + data.cell.height - 3;
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(255, 255, 255);
            // Rotér tekst 90° modurs så den læses nedefra-op
            pdf.text(String(headerText), cx, cy, { align: 'left', angle: 90, baseline: 'middle' });
          }
        },
      });

      // Footer med dato + sidetal
      const pageCount = pdf.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(130, 130, 130);
        pdf.setDrawColor(220, 220, 220);
        pdf.line(12, 198, 285, 198);
        pdf.text(`Udskrevet: ${dateStr}`, 12, 204);
        pdf.text(`Side ${i}/${pageCount}`, 285, 204, { align: 'right' });
      }

      pdf.save(`bestillingsliste-${(eventName || 'event').replace(/[^a-zæøåA-ZÆØÅ0-9_-]+/gi, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("Bestillingsliste PDF eksporteret!");
    } catch (err) {
      console.error(err);
      toast.error("Fejl ved bestillingsliste-generering");
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      const el = analyticsRef.current; if (!el) return;
      const bgColor = printMode ? '#ffffff' : '#0a0a1a';
      const canvas = await html2canvas(el, { backgroundColor: bgColor, scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = 210, imgW = pw - 20, imgH = (canvas.height * imgW) / canvas.width;
      if (printMode) {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pw, 295, 'F');
        pdf.setTextColor(0, 0, 0);
      } else {
        pdf.setFillColor(26, 26, 46);
        pdf.rect(0, 0, pw, 295, 'F');
        pdf.setTextColor(255, 255, 255);
      }
      pdf.setFontSize(18);
      pdf.text('Resovia Analytics', pw/2, 15, { align: 'center' });
      pdf.setFontSize(10); pdf.text(`Genereret: ${new Date().toLocaleDateString('da-DK')}`, pw/2, 22, { align: 'center' });
      pdf.addImage(imgData, 'PNG', 10, 30, imgW, imgH);
      pdf.save(`resovia-analytics-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success("PDF genereret!");
    } catch { toast.error("Fejl ved PDF-generering"); }
    setExporting(false);
  };

  const exportToExcel = () => {
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const filterInfo = [
        ['Filter', 'Værdi'],
        ['Typer', selectedTypes.map(t => TYPE_LABELS[t]).join(', ')],
        ['Område', selectedArea ? availableAreas.find(a => a.id === selectedArea)?.name || 'Ukendt' : 'Alle'],
        ['Zone', selectedZone ? availableZones.find(z => z.id === selectedZone)?.name || 'Ukendt' : 'Alle'],
        ['Periode', timeInterval === 'all' ? 'Alt' : timeInterval],
        ['Eksporteret', new Date().toLocaleString('da-DK')],
        []
      ];

      // === Overblik sheet ===
      const overblicRows = [
        ...filterInfo,
        ['Status', 'Antal'],
        ['Total', computedStats.total],
        ['Fuldført', computedStats.completed],
        ['I gang', computedStats.in_progress],
        ['Venter', computedStats.pending],
        [],
        ['Bestillingsmetode', 'Antal'],
        ['QR Scan', computedStats.qr],
        ['Manuel', computedStats.manual],
      ];
      if (extendedStats?.active_users?.by_role) {
        overblicRows.push([], ['Aktive brugere per rolle', 'Antal']);
        Object.entries(extendedStats.active_users.by_role).forEach(([role, count]) => overblicRows.push([role, count]));
        overblicRows.push(['TOTAL', extendedStats.active_users.total]);
      }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(overblicRows), 'Overblik');

      // === Aktivitet sheet ===
      const aktivRows = [
        ...filterInfo,
        ['Gennemsnitlig responstid (min)', extendedStats?.response_time?.average_minutes || 0],
        ['Målinger', extendedStats?.response_time?.sample_size || 0],
        [],
        ['Enhed', 'Type', 'Total', 'Fuldført', 'Venter'],
      ];
      Object.entries(perPointStats).sort(([,a],[,b]) => b.total - a.total).forEach(([name, data]) => {
        const pType = pointTypeMap[name] || 'ressourceø';
        aktivRows.push([name, TYPE_LABELS[pType], data.total, data.completed, data.pending]);
      });
      aktivRows.push(['TOTAL', '', 
        Object.values(perPointStats).reduce((a,b) => a + b.total, 0),
        Object.values(perPointStats).reduce((a,b) => a + b.completed, 0),
        Object.values(perPointStats).reduce((a,b) => a + b.pending, 0)
      ]);
      if (extendedStats?.response_time?.by_point && Object.keys(extendedStats.response_time.by_point).length > 0) {
        aktivRows.push([], ['Responstid per enhed', 'Minutter']);
        Object.entries(extendedStats.response_time.by_point).sort(([,a],[,b]) => b - a).forEach(([name, mins]) => aktivRows.push([name, mins]));
      }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aktivRows), 'Aktivitet & Responstid');

      // === Affald & Fraktioner sheet ===
      const affaldRows = [
        ...filterInfo,
        ['Total liter', extendedStats?.liter_stats?.total_liters || 0],
        [],
        ['Fraktion', 'Liter', 'Tømninger'],
      ];
      const fractions = extendedStats?.liter_stats?.by_fraction || {};
      const emptyingByFrac = extendedStats?.emptying_stats?.by_fraction || {};
      Object.keys({...fractions, ...emptyingByFrac}).forEach(frac => {
        affaldRows.push([frac, fractions[frac] || 0, emptyingByFrac[frac] || 0]);
      });
      affaldRows.push(['TOTAL', extendedStats?.liter_stats?.total_liters || 0, Object.values(emptyingByFrac).reduce((a,b) => a+b, 0)]);

      if (extendedStats?.liter_stats?.by_type) {
        affaldRows.push([], ['Liter per type', 'Liter']);
        Object.entries(extendedStats.liter_stats.by_type).filter(([t]) => selectedTypes.includes(t)).forEach(([t, v]) => affaldRows.push([TYPE_LABELS[t], v]));
      }
      if (extendedStats?.liter_stats?.by_area) {
        affaldRows.push([], ['Liter per område', 'Liter']);
        Object.entries(extendedStats.liter_stats.by_area).forEach(([area, v]) => affaldRows.push([area, v]));
      }
      if (extendedStats?.liter_stats?.by_point) {
        affaldRows.push([], ['Liter per enhed', 'Liter', 'Tømninger', 'Type']);
        Object.entries(extendedStats.liter_stats.by_point)
          .filter(([name]) => selectedTypes.includes(pointTypeMap[name] || 'ressourceø'))
          .sort(([,a],[,b]) => (b.liters || b) - (a.liters || a))
          .forEach(([name, v]) => {
            affaldRows.push([name, typeof v === 'object' ? v.liters : v, typeof v === 'object' ? v.count : '', TYPE_LABELS[pointTypeMap[name]] || '']);
          });
      }
      if (extendedStats?.sorting_quality) {
        affaldRows.push([], ['Sorteringskvalitet', 'Antal']);
        affaldRows.push(['God', extendedStats.sorting_quality.totals?.good || 0]);
        affaldRows.push(['OK', extendedStats.sorting_quality.totals?.medium || 0]);
        affaldRows.push(['Dårlig', extendedStats.sorting_quality.totals?.poor || 0]);
      }
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(affaldRows), 'Affald & Fraktioner');

      // === Top 10 sheet ===
      const topRows = [...filterInfo];
      selectedTypes.forEach(type => {
        const typePoints = Object.entries(perPointStats).filter(([name]) => (pointTypeMap[name] || 'ressourceø') === type).sort(([,a],[,b]) => b.total - a.total).slice(0, 10);
        if (typePoints.length > 0) {
          topRows.push([`Top 10 - ${TYPE_LABELS[type]}`, 'Antal']);
          typePoints.forEach(([name, data]) => topRows.push([name, data.total]));
          topRows.push([]);
        }
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(topRows), 'Top 10');

      // === Planlægning: Ressourceøer sheet ===
      if (planningData?.rows?.length > 0) {
        const sizes = planningData.containerSizes || [];
        const resHeader = ['Punkt', 'Fraktion', ...sizes, 'Total', 'Bestilt ☐'];
        const resRows = [resHeader];
        planningData.rows.forEach(row => {
          const total = Object.values(row.sizes).reduce((a,b) => a+b, 0);
          resRows.push([row.pointName, row.fraction, ...sizes.map(s => row.sizes[s] || 0), total, '☐']);
        });
        // Totalrække
        const totalRow = ['TOTAL', '', ...sizes.map(s => planningData.rows.reduce((a, r) => a + (r.sizes[s] || 0), 0)), planningData.totalContainers, ''];
        resRows.push(totalRow);
        // Skilte-oversigt
        resRows.push([]);
        resRows.push(['SKILTE OVERSIGT']);
        resRows.push(['Fraktion', 'Antal containere', 'Skilte']);
        Object.entries(planningData.totals.byFraction).sort((a,b) => b[1]-a[1]).forEach(([f, c]) => {
          resRows.push([f, c, planningData.signsByFraction?.[f] || 0]);
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resRows), 'Ressourceøer');
      }

      // === Planlægning: Ressourcespande sheet ===
      if (binPlanningData?.rows?.length > 0) {
        const binSizes = binPlanningData.containerSizes || [];
        const binHeader = ['Punkt', 'Fraktion', ...binSizes, 'Total', 'Bestilt ☐'];
        const binExportRows = [binHeader];
        binPlanningData.rows.forEach(row => {
          const total = Object.values(row.sizes).reduce((a,b) => a+b, 0);
          binExportRows.push([row.pointName, row.fraction, ...binSizes.map(s => row.sizes[s] || 0), total, '☐']);
        });
        const binTotalRow = ['TOTAL', '', ...binSizes.map(s => binPlanningData.rows.reduce((a, r) => a + (r.sizes[s] || 0), 0)), binPlanningData.totalBins, ''];
        binExportRows.push(binTotalRow);
        binExportRows.push([]);
        binExportRows.push(['SKILTE OVERSIGT']);
        binExportRows.push(['Fraktion', 'Antal spande', 'Skilte']);
        Object.entries(binPlanningData.totals.byFraction).sort((a,b) => b[1]-a[1]).forEach(([f, c]) => {
          binExportRows.push([f, c, binPlanningData.signsByFraction?.[f] || 0]);
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(binExportRows), 'Ressourcespande');
      }

      // === Planlægning: Maxicontainere sheet ===
      if (maxiPlanningData?.rows?.length > 0) {
        const maxiTypes = maxiPlanningData.containerTypes || [];
        const maxiHeader = ['Maxicontainer', 'Fraktion', ...maxiTypes, 'Total', 'Bestilt ☐'];
        const maxiExportRows = [maxiHeader];
        maxiPlanningData.rows.forEach(row => {
          const total = Object.values(row.sizes).reduce((a,b) => a+b, 0);
          maxiExportRows.push([row.pointName, row.fraction, ...maxiTypes.map(t => row.sizes[t] || 0), total, '☐']);
        });
        const maxiTotalRow = ['TOTAL', '', ...maxiTypes.map(t => maxiPlanningData.rows.reduce((a, r) => a + (r.sizes[t] || 0), 0)), maxiPlanningData.totalMaxi, ''];
        maxiExportRows.push(maxiTotalRow);
        maxiExportRows.push([]);
        maxiExportRows.push(['SKILTE OVERSIGT']);
        maxiExportRows.push(['Fraktion', 'Antal', 'Skilte']);
        Object.entries(maxiPlanningData.totals.byFraction).sort((a,b) => b[1]-a[1]).forEach(([f, c]) => {
          maxiExportRows.push([f, c, maxiPlanningData.signsByFraction?.[f] || 0]);
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(maxiExportRows), 'Maxicontainere');
      }

      XLSX.writeFile(wb, `resovia-analytics-${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("Excel eksporteret!");
    } catch (err) {
      console.error(err);
      toast.error("Fejl ved Excel-eksport");
    }
    setExporting(false);
  };

  const exportToImage = async () => {
    setExporting(true);
    try {
      const el = analyticsRef.current; if (!el) return;
      const canvas = await html2canvas(el, { backgroundColor: printMode ? '#ffffff' : '#0a0a1a', scale: 2 });
      const link = document.createElement('a');
      link.download = `resovia-analytics-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL(); link.click();
      toast.success("Billede eksporteret!");
    } catch { toast.error("Fejl ved eksport"); }
    setExporting(false);
  };

  const toggleType = (type) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // Nulstil analytics
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetPin, setResetPin] = useState('');
  const [resetting, setResetting] = useState(false);
  const handleResetAnalytics = async () => {
    if (resetPin.length !== 4) { toast.error("Indtast en 4-cifret PIN"); return; }
    setResetting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/analytics/reset`, { pin: resetPin }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setShowResetDialog(false);
      setResetPin('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved nulstilling");
    } finally { setResetting(false); }
  };

  const availableAreas = extendedStats?.areas || [];
  const availableZones = (extendedStats?.work_zones || []).filter(z => !selectedArea || z.area_id === selectedArea);
  const availableUnits = (extendedStats?.points_meta || []).filter(p => {
    if (!selectedTypes.includes(p.point_type)) return false;
    if (selectedArea && p.area_id !== selectedArea) return false;
    return true;
  });

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <RefreshCw className="h-8 w-8 animate-spin text-primary" />
    </div>
  );

  const tabs = [
    { id: 'overblik', label: 'Overblik', icon: TrendingUp },
    { id: 'aktivitet', label: 'Aktivitet & Responstid', icon: Clock },
    { id: 'affald', label: 'Affald & Fraktioner', icon: Droplets },
    { id: 'planning', label: 'Planlægning', icon: Package },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="analytics-page">
      {/* HEADER */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex flex-wrap items-center gap-2 md:gap-3">
          <Button data-testid="back-button" onClick={() => navigate(eventId ? `/event/${eventId}` : "/")} variant="ghost" size="sm"><ArrowLeft className="h-4 w-4" /></Button>
          <div className="flex-1 min-w-[140px]">
            <h1 className="text-xl font-bold font-heading uppercase tracking-tight">Analytics</h1>
            <p className="text-xs text-muted-foreground font-mono">Statistik og rapportering</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={loadData}><RefreshCw className="h-4 w-4 mr-1" />Opdater</Button>
            {!isGuest && <>
              <Button variant="outline" size="sm" onClick={exportToExcel} disabled={exporting} data-testid="export-excel-btn"><FileDown className="h-4 w-4 mr-1" />Excel</Button>
              <Button variant="outline" size="sm" onClick={exportToImage} disabled={exporting}><Image className="h-4 w-4 mr-1" />PNG</Button>
              <Button variant="outline" size="sm" onClick={exportToPDF} disabled={exporting} data-testid="export-pdf-btn"><FileDown className="h-4 w-4 mr-1" />{exporting ? '...' : 'PDF'}</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/print-map?event_id=${eventId || ''}`)} data-testid="print-map-btn" title="Åbn printvenlig kortvisning"><Printer className="h-4 w-4 mr-1" />Print kort</Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/print-all-qr?event_id=${eventId || ''}`)} data-testid="print-all-qr-btn" title="Hent og print alle QR-koder på én gang"><Printer className="h-4 w-4 mr-1" />Alle QR-koder</Button>
              <Button variant="destructive" size="sm" onClick={() => setShowResetDialog(true)} data-testid="reset-analytics-btn"><AlertCircle className="h-4 w-4 mr-1" />Nulstil</Button>
            </>}
          </div>
        </div>

        {/* TABS */}
        <div className="container mx-auto px-4 pb-2 flex gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <Button key={tab.id} variant={activeView === tab.id ? 'default' : 'outline'} size="sm"
              onClick={() => { setActiveView(tab.id); if (tab.id === 'planning') loadPlanningData(); }}
              data-testid={`tab-${tab.id}`} className="whitespace-nowrap">
              <tab.icon className="h-4 w-4 mr-1.5" />{tab.label}
            </Button>
          ))}
        </div>
      </header>

      {/* GLOBAL FILTER BAR */}
      {activeView !== 'planning' && (
      <div className="sticky top-[105px] z-40 bg-background/95 backdrop-blur-sm border-b border-border/50">
        <div className="container mx-auto px-4 py-3 space-y-2">
          {/* Row 1: Type + Area + Zone */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* Type toggles */}
            <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1">
              <span className="text-xs text-muted-foreground px-2"><Filter className="h-3 w-3 inline mr-1" />Type:</span>
              {Object.entries(TYPE_LABELS).map(([type, label]) => (
                <button key={type} onClick={() => toggleType(type)} data-testid={`filter-type-${type}`}
                  className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 transition-all ${
                    selectedTypes.includes(type) ? 'text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'
                  }`} style={selectedTypes.includes(type) ? { backgroundColor: TYPE_COLORS[type] } : {}}>
                  {label}
                </button>
              ))}
            </div>

            {/* Area dropdown */}
            <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1 px-2">
              <span className="text-xs text-muted-foreground"><MapPin className="h-3 w-3 inline mr-1" />Område:</span>
              <select value={selectedArea} onChange={e => { setSelectedArea(e.target.value); setSelectedZone(''); setSelectedUnits([]); }}
                className="bg-transparent text-xs outline-none cursor-pointer" style={{colorScheme: 'dark'}} data-testid="filter-area">
                <option value="" style={{background:'#18181b'}}>Alle</option>
                {availableAreas.map(a => <option key={a.id} value={a.id} style={{background:'#18181b'}}>{a.name}</option>)}
              </select>
            </div>

            {/* Zone dropdown */}
            {availableZones.length > 0 && (
              <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1 px-2">
                <span className="text-xs text-muted-foreground">Zone:</span>
                <select value={selectedZone} onChange={e => setSelectedZone(e.target.value)}
                  className="bg-transparent text-xs outline-none cursor-pointer" style={{colorScheme: 'dark'}} data-testid="filter-zone">
                  <option value="" style={{background:'#18181b'}}>Alle</option>
                  {availableZones.map(z => <option key={z.id} value={z.id} style={{background:'#18181b'}}>{z.name}</option>)}
                </select>
              </div>
            )}

            {/* Unit multi-select */}
            <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1 px-2">
              <span className="text-xs text-muted-foreground">Enheder:</span>
              <select value="" onChange={e => { const id = e.target.value; if (id && !selectedUnits.includes(id)) setSelectedUnits([...selectedUnits, id]); }}
                className="bg-transparent text-xs outline-none cursor-pointer" style={{colorScheme: 'dark'}} data-testid="filter-units">
                <option value="" style={{background:'#18181b'}}>Alle ({availableUnits.length})</option>
                {availableUnits.map(u => <option key={u.id} value={u.id} style={{background:'#18181b'}}>{u.name} ({TYPE_LABELS[u.point_type]?.substring(0,3)})</option>)}
              </select>
              {selectedUnits.length > 0 && (
                <button onClick={() => setSelectedUnits([])} className="text-xs text-destructive hover:underline ml-1">Nulstil ({selectedUnits.length})</button>
              )}
            </div>
          </div>

          {/* Row 2: Time + Compare */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
              <span className="text-xs text-muted-foreground px-2"><Calendar className="h-3 w-3 inline mr-1" />Periode:</span>
              {['all', 'day', 'week', 'hour'].map(id => (
                <button key={id} onClick={() => setTimeInterval(id)} className={`px-2 py-1 rounded text-xs ${timeInterval === id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  {id === 'all' ? 'Alt' : id === 'day' ? 'Dag' : id === 'week' ? 'Uge' : 'Time'}
                </button>
              ))}
            </div>

            {timeInterval === 'day' && (
              <div className="bg-card border border-border rounded-lg p-1 px-2">
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-transparent text-xs outline-none cursor-pointer" data-testid="date-picker" />
              </div>
            )}

            {timeInterval === 'week' && (
              <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1 px-2">
                <select value={selectedYear} onChange={e => setSelectedYear(+e.target.value)} className="bg-transparent text-xs outline-none" style={{colorScheme: 'dark'}}>
                  {[2024,2025,2026,2027].map(y => <option key={y} style={{background:'#18181b'}}>{y}</option>)}
                </select>
                <select value={selectedWeek} onChange={e => setSelectedWeek(+e.target.value)} className="bg-transparent text-xs outline-none" style={{colorScheme: 'dark'}}>
                  {Array.from({length:52},(_,i)=>i+1).map(w => <option key={w} value={w} style={{background:'#18181b'}}>Uge {w}</option>)}
                </select>
              </div>
            )}

            {timeInterval === 'hour' && (
              <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1 px-2">
                <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="bg-transparent text-xs outline-none cursor-pointer" />
                <select value={selectedHour} onChange={e => setSelectedHour(+e.target.value)} className="bg-transparent text-xs outline-none" style={{colorScheme: 'dark'}}>
                  {Array.from({length:24},(_,i)=>i).map(h => <option key={h} value={h} style={{background:'#18181b'}}>{String(h).padStart(2,'0')}:00</option>)}
                </select>
              </div>
            )}

            <button onClick={() => setCompareMode(!compareMode)} disabled={timeInterval === 'all'}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1.5 border ${
                compareMode && timeInterval !== 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:bg-muted'
              } ${timeInterval === 'all' ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="compare-toggle">
              <TrendingUp className="h-3 w-3" />Sammenlign
            </button>

            {compareMode && timeInterval !== 'all' && (
              <div className="flex items-center gap-1 bg-blue-500/10 border border-blue-500/30 rounded-lg p-1 px-2">
                <span className="text-xs text-blue-400">vs:</span>
                {timeInterval === 'day' && <input type="date" value={compareDate} onChange={e => setCompareDate(e.target.value)} className="bg-transparent text-xs outline-none text-blue-300" data-testid="compare-date-picker" />}
                {timeInterval === 'week' && <>
                  <select value={compareYear} onChange={e => setCompareYear(+e.target.value)} className="bg-transparent text-xs outline-none text-blue-300" style={{colorScheme: 'dark'}}>{[2024,2025,2026,2027].map(y => <option key={y} style={{background:'#18181b'}}>{y}</option>)}</select>
                  <select value={compareWeek} onChange={e => setCompareWeek(+e.target.value)} className="bg-transparent text-xs outline-none text-blue-300" style={{colorScheme: 'dark'}}>{Array.from({length:52},(_,i)=>i+1).map(w => <option key={w} value={w} style={{background:'#18181b'}}>Uge {w}</option>)}</select>
                </>}
                {timeInterval === 'hour' && <>
                  <input type="date" value={compareDate} onChange={e => setCompareDate(e.target.value)} className="bg-transparent text-xs outline-none text-blue-300" />
                  <select value={compareHour} onChange={e => setCompareHour(+e.target.value)} className="bg-transparent text-xs outline-none text-blue-300" style={{colorScheme: 'dark'}}>{Array.from({length:24},(_,i)=>i).map(h => <option key={h} value={h} style={{background:'#18181b'}}>{String(h).padStart(2,'0')}:00</option>)}</select>
                </>}
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      <main className="container mx-auto px-4 py-6" ref={analyticsRef}>

        {/* ====== TAB: OVERBLIK ====== */}
        {activeView === 'overblik' && (
          <div className="space-y-6">
            {/* Status kort */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'TOTAL', val: computedStats.total, cmp: compareStats?.total, icon: TrendingUp, color: 'text-primary' },
                { label: 'FULDFØRT', val: computedStats.completed, cmp: compareStats?.completed, icon: CheckCircle2, color: 'text-green-500' },
                { label: 'I GANG', val: computedStats.in_progress, icon: Clock, color: 'text-yellow-500' },
                { label: 'VENTER', val: computedStats.pending, icon: AlertCircle, color: 'text-red-500' },
              ].map(card => (
                <div key={card.label} className="bg-card border border-border rounded-lg p-4" data-testid={`stat-${card.label.toLowerCase()}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <card.icon className={`h-4 w-4 ${card.color}`} />
                    <span className="text-xs text-muted-foreground">{card.label}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className={`text-2xl font-bold ${card.color}`}>{card.val}</p>
                    {compareMode && card.cmp !== undefined && calcChange(card.val, card.cmp) !== null && (
                      <span className={`text-xs font-medium ${calcChange(card.val, card.cmp) > 0 ? 'text-green-500' : calcChange(card.val, card.cmp) < 0 ? 'text-red-500' : 'text-muted-foreground'}`}>
                        {calcChange(card.val, card.cmp) > 0 ? '+' : ''}{calcChange(card.val, card.cmp)}%
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Top 10 per type */}
            <div className="space-y-4">
              <h2 className="text-lg font-bold flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" />Top 10 mest travle</h2>
              {selectedTypes.map(type => {
                const typePoints = Object.entries(perPointStats).filter(([name]) => {
                  return (pointTypeMap[name] || 'ressourceø') === type;
                }).sort(([,a],[,b]) => b.total - a.total).slice(0, 10);
                if (typePoints.length === 0) return null;
                return (
                  <StatChartCard key={type} title={`${TYPE_LABELS[type]} - Top 10`}
                    data={Object.fromEntries(typePoints.map(([n, d]) => [n, d.total]))}
                    defaultChartType="bar_vertical" />
                );
              })}
            </div>

            {/* Aktive brugere */}
            {extendedStats?.active_users && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><Users className="h-4 w-4 text-primary" />Aktive brugere ({extendedStats.active_users.total})</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(extendedStats.active_users.by_role || {}).sort(([,a],[,b])=>b-a).map(([role, count]) => (
                    <span key={role} className="px-3 py-1.5 bg-muted rounded text-xs font-medium">{role}: <strong>{count}</strong></span>
                  ))}
                  {Object.keys(extendedStats.active_users.by_role || {}).length === 0 && <span className="text-muted-foreground text-xs">Ingen aktive</span>}
                </div>
              </div>
            )}

            {/* Bestillingsmetode */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatChartCard title="Bestillingsmetode" data={{ 'QR Scan': computedStats.qr, 'Manuel': computedStats.manual }} defaultChartType="pie" />
              
              {/* Per type breakdown */}
              {extendedStats?.requests_by_type && Object.keys(extendedStats.requests_by_type).length > 0 && (
                <StatChartCard title="Anmodninger per type"
                  data={Object.fromEntries(Object.entries(extendedStats.requests_by_type).filter(([t]) => selectedTypes.includes(t)).map(([t, d]) => [TYPE_LABELS[t] || t, d.total]))}
                  defaultChartType="bar" />
              )}
            </div>

            {/* Besked-statistik */}
            {extendedStats?.message_stats && (
              <div className="bg-card border border-border rounded-lg p-4">
                <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-indigo-500" />Besked-statistik</h3>
                <div className="grid grid-cols-3 gap-3 mb-3">
                  <div className="text-center p-2 bg-muted/30 rounded"><p className="text-xl font-bold text-indigo-400">{extendedStats.message_stats.total}</p><p className="text-xs text-muted-foreground">Total</p></div>
                  <div className="text-center p-2 bg-muted/30 rounded"><p className="text-xl font-bold text-yellow-400">{extendedStats.message_stats.from_leaders}</p><p className="text-xs text-muted-foreground">Fra ledere</p></div>
                  <div className="text-center p-2 bg-muted/30 rounded"><p className="text-xl font-bold text-green-400">{extendedStats.message_stats.from_others}</p><p className="text-xs text-muted-foreground">Fra andre</p></div>
                </div>
                {Object.keys(extendedStats.message_stats.by_role || {}).length > 0 && (
                  <div className="flex flex-wrap gap-2">{Object.entries(extendedStats.message_stats.by_role).sort(([,a],[,b])=>b-a).map(([r,c]) => (
                    <span key={r} className="px-2 py-1 bg-muted rounded text-xs">{r}: {c}</span>
                  ))}</div>
                )}
              </div>
            )}

            {/* Seneste anmodninger (read-only) */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="text-sm font-medium mb-3">Seneste anmodninger</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {globalFiltered.slice(0, 15).map(req => (
                  <div key={req.id} className="flex items-center justify-between p-3 bg-muted/20 rounded">
                    <div>
                      <p className="text-sm font-medium">{req.point_name}</p>
                      <p className="text-xs text-muted-foreground">{new Date(req.requested_at).toLocaleString('da-DK')}</p>
                    </div>
                    <span className={`w-3 h-3 rounded-full ${req.status === 'grøn' ? 'bg-green-500' : req.status === 'gul' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                  </div>
                ))}
                {globalFiltered.length === 0 && <p className="text-muted-foreground text-sm text-center py-4">Ingen anmodninger</p>}
              </div>
            </div>
          </div>
        )}

        {/* ====== TAB: AKTIVITET & RESPONSTID ====== */}
        {activeView === 'aktivitet' && (
          <div className="space-y-6">
            {/* Responstid oversigt */}
            <div className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-lg p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-500/20 rounded-full"><Clock className="h-6 w-6 text-orange-400" /></div>
                <div>
                  <p className="text-3xl font-bold text-orange-400">{extendedStats?.response_time?.average_minutes || 0} min</p>
                  <p className="text-sm text-muted-foreground">Gennemsnitlig responstid ({extendedStats?.response_time?.sample_size || 0} målinger)</p>
                </div>
              </div>
            </div>

            {/* Responstid per type */}
            {extendedStats?.response_time?.by_type && Object.keys(extendedStats.response_time.by_type).length > 0 && (
              <StatChartCard title="Responstid per type (min)"
                data={Object.fromEntries(Object.entries(extendedStats.response_time.by_type).filter(([t]) => selectedTypes.includes(t)).map(([t, v]) => [TYPE_LABELS[t] || t, v]))}
                defaultChartType="bar" />
            )}

            {/* Responstid per enhed */}
            {extendedStats?.response_time?.by_point && Object.keys(extendedStats.response_time.by_point).length > 0 && (
              <StatChartCard title="Responstid per enhed (min)"
                data={Object.fromEntries(
                  Object.entries(extendedStats.response_time.by_point)
                    .filter(([name]) => {
                      return selectedTypes.includes(pointTypeMap[name] || 'ressourceø');
                    })
                    .sort(([,a],[,b]) => b - a).slice(0, 20)
                )}
                formatValue={v => `${v} min`}
                defaultChartType="bar" />
            )}

            {/* Aktivitetstabel */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="p-3 bg-muted/30 border-b border-border">
                <h3 className="text-sm font-medium">Alle enheder ({Object.keys(perPointStats).length})</h3>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/20 sticky top-0"><tr>
                    <th className="text-left p-2">Navn</th><th className="text-left p-2">Type</th>
                    <th className="text-center p-2">Total</th><th className="text-center p-2">Fuldført</th><th className="text-center p-2">Venter</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(perPointStats).sort(([,a],[,b])=>b.total-a.total).map(([name, data]) => {
                      const pType = pointTypeMap[name] || 'ressourceø';
                      return (
                        <tr key={name} className="border-b border-border/30 hover:bg-muted/10">
                          <td className="p-2 truncate max-w-[200px]" title={name}>{name}</td>
                          <td className="p-2 text-xs"><span className="px-1.5 py-0.5 rounded" style={{ backgroundColor: TYPE_COLORS[pType] + '33', color: TYPE_COLORS[pType] }}>{TYPE_LABELS[pType]?.substring(0,5) || '?'}</span></td>
                          <td className="text-center p-2 font-bold">{data.total}</td>
                          <td className="text-center p-2 text-green-500">{data.completed}</td>
                          <td className="text-center p-2 text-red-500">{data.pending}</td>
                        </tr>
                      );
                    })}
                    {/* TOTAL ROW */}
                    <tr className="bg-muted font-bold border-t-2 border-primary">
                      <td className="p-2" colSpan={2}>TOTAL</td>
                      <td className="text-center p-2">{Object.values(perPointStats).reduce((a,b) => a + b.total, 0)}</td>
                      <td className="text-center p-2 text-green-500">{Object.values(perPointStats).reduce((a,b) => a + b.completed, 0)}</td>
                      <td className="text-center p-2 text-red-500">{Object.values(perPointStats).reduce((a,b) => a + b.pending, 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ====== TAB: AFFALD & FRAKTIONER ====== */}
        {activeView === 'affald' && extendedStats && (
          <div className="space-y-6">
            {/* Total liter */}
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center gap-3">
                <Droplets className="h-8 w-8 text-blue-400" />
                <div>
                  <p className="text-3xl font-bold text-blue-400">{formatLiters(extendedStats.liter_stats?.total_liters || 0)}</p>
                  <p className="text-sm text-muted-foreground">Total affald håndteret</p>
                </div>
              </div>
            </div>

            {/* Liter + tømninger per fraktion (COMBINED) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <StatChartCard title="Liter per fraktion"
                data={extendedStats.liter_stats?.by_fraction || {}}
                formatValue={formatLiters} defaultChartType="pie" />
              <StatChartCard title="Tømninger per fraktion"
                data={extendedStats.emptying_stats?.by_fraction || {}}
                defaultChartType="bar" />
            </div>

            {/* Per type breakdown */}
            {extendedStats.liter_stats?.by_type && Object.keys(extendedStats.liter_stats.by_type).length > 0 && (
              <StatChartCard title="Liter per type"
                data={Object.fromEntries(Object.entries(extendedStats.liter_stats.by_type).filter(([t]) => selectedTypes.includes(t)).map(([t,v]) => [TYPE_LABELS[t] || t, v]))}
                formatValue={formatLiters} defaultChartType="bar" />
            )}

            {/* Per område */}
            <StatChartCard title="Liter per område"
              data={extendedStats.liter_stats?.by_area || {}}
              formatValue={formatLiters} defaultChartType="pie" />

            {/* Per enhed */}
            {extendedStats.liter_stats?.by_point && Object.keys(extendedStats.liter_stats.by_point).length > 0 && (
              <StatChartCard title="Liter per enhed (Top 15)"
                data={Object.fromEntries(
                  Object.entries(extendedStats.liter_stats.by_point)
                    .filter(([name]) => {
                      return selectedTypes.includes(pointTypeMap[name] || 'ressourceø');
                    })
                    .sort(([,a],[,b]) => (b.liters || b) - (a.liters || a))
                    .slice(0, 15)
                    .map(([n, v]) => [n, typeof v === 'object' ? v.liters : v])
                )}
                formatValue={formatLiters} defaultChartType="bar_vertical" />
            )}

            {/* Sorteringskvalitet */}
            {extendedStats.sorting_quality && (
              <div>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2"><ThumbsUp className="h-5 w-5 text-green-500" />Sorteringskvalitet</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
                    <ThumbsUp className="h-6 w-6 text-green-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-green-400">{extendedStats.sorting_quality.totals?.good || 0}</p>
                    <p className="text-sm text-muted-foreground">God</p>
                  </div>
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 text-center">
                    <Minus className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-yellow-400">{extendedStats.sorting_quality.totals?.medium || 0}</p>
                    <p className="text-sm text-muted-foreground">OK</p>
                  </div>
                  <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 text-center">
                    <ThumbsDown className="h-6 w-6 text-red-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-red-400">{extendedStats.sorting_quality.totals?.poor || 0}</p>
                    <p className="text-sm text-muted-foreground">Dårlig</p>
                  </div>
                </div>

                {/* Per type */}
                {extendedStats.sorting_quality.by_type && Object.keys(extendedStats.sorting_quality.by_type).length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-medium mb-3">Sorteringskvalitet per type</h3>
                    <div className="space-y-3">
                      {Object.entries(extendedStats.sorting_quality.by_type).filter(([t]) => selectedTypes.includes(t)).map(([type, counts]) => {
                        const t = counts.good + counts.medium + counts.poor;
                        return (
                          <div key={type}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm" style={{ color: TYPE_COLORS[type] }}>{TYPE_LABELS[type]}</span>
                              <span className="text-xs text-muted-foreground">{t > 0 ? Math.round((counts.good/t)*100) : 0}% god</span>
                            </div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                              {t > 0 && <><div className="bg-green-500" style={{width:`${(counts.good/t)*100}%`}}/><div className="bg-yellow-500" style={{width:`${(counts.medium/t)*100}%`}}/><div className="bg-red-500" style={{width:`${(counts.poor/t)*100}%`}}/></>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Per område */}
                {Object.keys(extendedStats.sorting_quality.by_area || {}).length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="text-sm font-medium mb-3">Sorteringskvalitet per område</h3>
                    <div className="space-y-3">
                      {Object.entries(extendedStats.sorting_quality.by_area).map(([area, counts]) => {
                        const t = counts.good + counts.medium + counts.poor;
                        return (
                          <div key={area}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm">{area}</span>
                              <span className="text-xs text-muted-foreground">{t > 0 ? Math.round((counts.good/t)*100) : 0}% god</span>
                            </div>
                            <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                              {t > 0 && <><div className="bg-green-500" style={{width:`${(counts.good/t)*100}%`}}/><div className="bg-yellow-500" style={{width:`${(counts.medium/t)*100}%`}}/><div className="bg-red-500" style={{width:`${(counts.poor/t)*100}%`}}/></>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Top 10 dårligst sorterede punkter i dagens vindue (06:00 → 06:00) */}
                {extendedStats.sorting_quality.by_point_today?.items?.length > 0 && (
                  <div className="bg-card border border-border rounded-lg p-4" data-testid="sorting-worst-today">
                    <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                      <h3 className="text-sm font-medium flex items-center gap-2">
                        <ThumbsDown className="h-4 w-4 text-red-500" />
                        Top 10 dårligst sorterede i dag
                      </h3>
                      <span className="text-[11px] text-muted-foreground font-mono">
                        {(() => {
                          const ws = extendedStats.sorting_quality.by_point_today.window_start;
                          const we = extendedStats.sorting_quality.by_point_today.window_end;
                          try {
                            const s = new Date(ws), e = new Date(we);
                            const fmt = (d) => d.toLocaleString('da-DK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' });
                            return `${fmt(s)} → ${fmt(e)}`;
                          } catch { return ''; }
                        })()}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mb-3">
                      Score = dårlig × 2 + acceptabel × 1. Bobler = sidste 7 dages udvikling (ældst → nyest), så du kan se om punkter forbedrer sig.
                    </p>
                    <div className="space-y-2">
                      {extendedStats.sorting_quality.by_point_today.items.map((p, idx) => (
                        <div
                          key={p.point_id}
                          className="border border-border rounded-md p-2.5 hover:bg-muted/30 transition-colors"
                          data-testid={`worst-point-${idx}`}
                        >
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500/15 text-red-400 text-xs font-bold flex-shrink-0">
                                {idx + 1}
                              </span>
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">
                                  {p.qr_ref ? `${p.qr_ref} · ` : ''}{p.name}
                                </div>
                                {p.area && <div className="text-[10px] text-muted-foreground truncate">{p.area}</div>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {p.medium > 0 && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400 text-[11px] font-mono">
                                  <span>Acceptabel</span><span className="font-bold">{p.medium}</span>
                                </span>
                              )}
                              {p.poor > 0 && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 text-[11px] font-mono">
                                  <span>Dårlig</span><span className="font-bold">{p.poor}</span>
                                </span>
                              )}
                            </div>
                          </div>
                          {/* 7-dages historik som mini-trend (rødt = dårlig, gult = ok, grønt = god) */}
                          {p.history && p.history.length > 0 && (
                            <div className="mt-2 flex items-center gap-1">
                              <span className="text-[9px] text-muted-foreground mr-1 uppercase tracking-wide">7d</span>
                              {p.history.map((d, di) => {
                                const total = d.good + d.medium + d.poor;
                                if (total === 0) {
                                  return <span key={di} className="w-3 h-3 rounded-sm bg-muted/60" title={`${d.date}: ingen aktivitet`} />;
                                }
                                // Dominerende status afgør farve
                                let color = 'bg-green-500';
                                if (d.poor >= d.good && d.poor >= d.medium) color = 'bg-red-500';
                                else if (d.medium >= d.good) color = 'bg-yellow-500';
                                return (
                                  <span
                                    key={di}
                                    className={`w-3 h-3 rounded-sm ${color}`}
                                    title={`${d.date}: god ${d.good} · ok ${d.medium} · dårlig ${d.poor}`}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ====== TAB: PLANLÆGNING ====== */}
        {activeView === 'planning' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div><h2 className="text-lg font-bold">Container- og Fraktionsoversigt</h2><p className="text-sm text-muted-foreground">Oversigt til bestilling hos renovationsselskab</p></div>
              <Button variant="outline" size="sm" onClick={loadPlanningData} disabled={loadingPlanning}><RefreshCw className={`h-4 w-4 mr-2 ${loadingPlanning ? 'animate-spin' : ''}`} />Opdater</Button>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-1 bg-muted/50 p-1 rounded-lg w-fit">
              {[
                { id: 'ressourceøer', label: 'Ressourceøer', icon: MapPin },
                { id: 'ressourcespande', label: 'Ressourcespande', icon: Trash2 },
                { id: 'maxicontainere', label: 'Maxicontainere', icon: Container },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => { setPlanningSubTab(tab.id); setFractionFilter(null); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    planningSubTab === tab.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                  data-testid={`planning-subtab-${tab.id}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {loadingPlanning ? (
              <div className="flex items-center justify-center py-16"><RefreshCw className="h-8 w-8 animate-spin text-primary" /></div>
            ) : (
              <>
                {/* === Ressourceøer === */}
                {planningSubTab === 'ressourceøer' && planningData && (
                  <div className="space-y-6" data-testid="planning-ressourceøer">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { val: planningData.totalContainers, label: 'Containere i alt' },
                        { val: Object.keys(planningData.totals.byFraction).length, label: 'Fraktionstyper' },
                        { val: planningData.containerSizes.length, label: 'Containerstørrelser' },
                        { val: planningData.pointCount, label: 'Ressourceøer' },
                      ].map((c, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 text-center">
                          <div className={`text-3xl font-bold ${i === 0 ? 'text-primary' : ''}`}>{c.val}</div>
                          <div className="text-sm text-muted-foreground">{c.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0 z-10"><tr>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%' }}>Ressourceø</th>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%', paddingLeft: '20mm' }}>Fraktion</th>
                            {planningData.containerSizes.map(s => <th key={s} className="text-center p-3 font-medium bg-muted">{s}</th>)}
                            <th className="text-center p-3 font-medium bg-primary/20">Total</th>
                          </tr></thead>
                          <tbody>
                            {planningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter).map((row, idx) => (
                              <tr key={`${row.pointId}-${row.fraction}`} className={idx % 2 === 0 ? 'bg-muted/20' : ''}>
                                <td className="p-3 font-medium whitespace-nowrap">{row.pointName}</td>
                                <td className="p-3 whitespace-nowrap" style={{ paddingLeft: '20mm' }}>{row.fraction}</td>
                                {planningData.containerSizes.map(s => <td key={s} className="text-center p-3">{row.sizes[s] || <span className="text-muted-foreground">-</span>}</td>)}
                                <td className="text-center p-3 font-bold bg-primary/10">{Object.values(row.sizes).reduce((a,b)=>a+b,0)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-muted font-bold"><tr>
                            <td className="p-3" colSpan={2}>TOTAL{fractionFilter ? ` (${fractionFilter})` : ''}</td>
                            {planningData.containerSizes.map(s => {
                              const filteredRows = planningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter);
                              const sizeTotal = filteredRows.reduce((a, r) => a + (r.sizes[s] || 0), 0);
                              return <td key={s} className="text-center p-3">{sizeTotal}</td>;
                            })}
                            <td className="text-center p-3 bg-primary/20">{
                              planningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter)
                                .reduce((a, r) => a + Object.values(r.sizes).reduce((x,y) => x+y, 0), 0)
                            }</td>
                          </tr></tfoot>
                        </table>
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Total per fraktion</h3>
                        {fractionFilter && <button onClick={() => setFractionFilter(null)} className="text-xs text-primary hover:underline" data-testid="clear-fraction-filter">Vis alle</button>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {Object.entries(planningData.totals.byFraction).sort((a,b)=>b[1]-a[1]).map(([f,c]) => {
                          // Beregn container-størrelsesfordeling for denne fraktion
                          const sizeBreakdown = {};
                          planningData.rows.filter(r => r.fraction === f).forEach(r => {
                            Object.entries(r.sizes).forEach(([s, n]) => {
                              sizeBreakdown[s] = (sizeBreakdown[s] || 0) + n;
                            });
                          });
                          return (
                          <div key={f}
                            onClick={() => setFractionFilter(fractionFilter === f ? null : f)}
                            className={`rounded p-3 text-center cursor-pointer transition-all ${fractionFilter === f ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted/50 hover:bg-muted/80'}`}
                            data-testid={`fraction-card-${f}`}
                          >
                            <div className="text-xl font-bold">{c}</div>
                            <div className="text-sm text-muted-foreground">{f}</div>
                            <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">Skilte: {planningData.signsByFraction?.[f] || 0}</div>
                            <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">
                              {Object.entries(sizeBreakdown).sort((a,b) => a[0].localeCompare(b[0])).map(([s, n], i) => (
                                <span key={s}>{i > 0 ? ' · ' : ''}{s}: {n}</span>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* === Ressourcespande === */}
                {planningSubTab === 'ressourcespande' && binPlanningData && (
                  <div className="space-y-6" data-testid="planning-ressourcespande">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { val: binPlanningData.totalBins, label: 'Spande i alt' },
                        { val: Object.keys(binPlanningData.totals.byFraction).length, label: 'Fraktionstyper' },
                        { val: binPlanningData.containerSizes.length, label: 'Containerstørrelser' },
                        { val: binPlanningData.pointCount, label: 'Ressourcespande' },
                      ].map((c, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 text-center">
                          <div className={`text-3xl font-bold ${i === 0 ? 'text-primary' : ''}`}>{c.val}</div>
                          <div className="text-sm text-muted-foreground">{c.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0 z-10"><tr>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%' }}>Ressourcespand</th>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%', paddingLeft: '20mm' }}>Fraktion</th>
                            {binPlanningData.containerSizes.map(s => <th key={s} className="text-center p-3 font-medium bg-muted">{s}</th>)}
                            <th className="text-center p-3 font-medium bg-primary/20">Total</th>
                          </tr></thead>
                          <tbody>
                            {binPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter).map((row, idx) => (
                              <tr key={`${row.pointId}-${row.fraction}`} className={idx % 2 === 0 ? 'bg-muted/20' : ''}>
                                <td className="p-3 font-medium whitespace-nowrap">{row.pointName}</td>
                                <td className="p-3 whitespace-nowrap" style={{ paddingLeft: '20mm' }}>{row.fraction}</td>
                                {binPlanningData.containerSizes.map(s => <td key={s} className="text-center p-3">{row.sizes[s] || <span className="text-muted-foreground">-</span>}</td>)}
                                <td className="text-center p-3 font-bold bg-primary/10">{Object.values(row.sizes).reduce((a,b)=>a+b,0)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-muted font-bold"><tr>
                            <td className="p-3" colSpan={2}>TOTAL{fractionFilter ? ` (${fractionFilter})` : ''}</td>
                            {binPlanningData.containerSizes.map(s => {
                              const filteredRows = binPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter);
                              const sizeTotal = filteredRows.reduce((a, r) => a + (r.sizes[s] || 0), 0);
                              return <td key={s} className="text-center p-3">{sizeTotal}</td>;
                            })}
                            <td className="text-center p-3 bg-primary/20">{
                              binPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter)
                                .reduce((a, r) => a + Object.values(r.sizes).reduce((x,y) => x+y, 0), 0)
                            }</td>
                          </tr></tfoot>
                        </table>
                      </div>
                    </div>
                    <div className="bg-card border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold">Total per fraktion</h3>
                        {fractionFilter && <button onClick={() => setFractionFilter(null)} className="text-xs text-primary hover:underline">Vis alle</button>}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {Object.entries(binPlanningData.totals.byFraction).sort((a,b)=>b[1]-a[1]).map(([f,c]) => {
                          const sizeBreakdown = {};
                          binPlanningData.rows.filter(r => r.fraction === f).forEach(r => {
                            Object.entries(r.sizes).forEach(([s, n]) => { sizeBreakdown[s] = (sizeBreakdown[s] || 0) + n; });
                          });
                          return (
                          <div key={f}
                            onClick={() => setFractionFilter(fractionFilter === f ? null : f)}
                            className={`rounded p-3 text-center cursor-pointer transition-all ${fractionFilter === f ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted/50 hover:bg-muted/80'}`}
                          >
                            <div className="text-xl font-bold">{c}</div>
                            <div className="text-sm text-muted-foreground">{f}</div>
                            <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">Skilte: {binPlanningData.signsByFraction?.[f] || 0}</div>
                            <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">
                              {Object.entries(sizeBreakdown).sort((a,b) => a[0].localeCompare(b[0])).map(([s, n], i) => (
                                <span key={s}>{i > 0 ? ' · ' : ''}{s}: {n}</span>
                              ))}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* === Maxicontainere === */}
                {planningSubTab === 'maxicontainere' && maxiPlanningData && (
                  <div className="space-y-6" data-testid="planning-maxicontainere">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { val: maxiPlanningData.totalMaxi, label: 'Maxicontainere i alt' },
                        { val: Object.keys(maxiPlanningData.totals.byFraction).length, label: 'Fraktionstyper' },
                        { val: maxiPlanningData.containerTypes.length, label: 'Container-typer' },
                        { val: maxiPlanningData.rows.length, label: 'Punkter' },
                      ].map((c, i) => (
                        <div key={i} className="bg-card border border-border rounded-lg p-4 text-center">
                          <div className={`text-3xl font-bold ${i === 0 ? 'text-primary' : ''}`}>{c.val}</div>
                          <div className="text-sm text-muted-foreground">{c.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                      <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50 sticky top-0 z-10"><tr>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%' }}>Maxicontainer</th>
                            <th className="text-left p-3 font-medium bg-muted whitespace-nowrap" style={{ width: '1%', paddingLeft: '20mm' }}>Fraktion</th>
                            {maxiPlanningData.containerTypes.map(t => <th key={t} className="text-center p-3 font-medium bg-muted whitespace-normal" style={{ minWidth: '120px' }}>{t}</th>)}
                            <th className="text-center p-3 font-medium bg-primary/20">Total</th>
                          </tr></thead>
                          <tbody>
                            {maxiPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter).map((row, idx) => (
                              <tr key={`${row.pointId}-${row.fraction}`} className={idx % 2 === 0 ? 'bg-muted/20' : ''}>
                                <td className="p-3 font-medium whitespace-nowrap">{row.pointName}</td>
                                <td className="p-3 whitespace-nowrap" style={{ paddingLeft: '20mm' }}>{row.fraction}</td>
                                {maxiPlanningData.containerTypes.map(t => <td key={t} className="text-center p-3">{row.sizes[t] || <span className="text-muted-foreground">-</span>}</td>)}
                                <td className="text-center p-3 font-bold bg-primary/10">{Object.values(row.sizes).reduce((a,b)=>a+b,0)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-muted font-bold"><tr>
                            <td className="p-3" colSpan={2}>TOTAL{fractionFilter ? ` (${fractionFilter})` : ''}</td>
                            {maxiPlanningData.containerTypes.map(t => {
                              const filteredRows = maxiPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter);
                              const typeTotal = filteredRows.reduce((a, r) => a + (r.sizes[t] || 0), 0);
                              return <td key={t} className="text-center p-3">{typeTotal}</td>;
                            })}
                            <td className="text-center p-3 bg-primary/20">{
                              maxiPlanningData.rows.filter(row => !fractionFilter || row.fraction === fractionFilter)
                                .reduce((a, r) => a + Object.values(r.sizes).reduce((x,y) => x+y, 0), 0)
                            }</td>
                          </tr></tfoot>
                        </table>
                      </div>
                    </div>
                    {Object.keys(maxiPlanningData.totals.byFraction).length > 0 && (
                      <div className="bg-card border border-border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-bold">Total per fraktion</h3>
                          {fractionFilter && <button onClick={() => setFractionFilter(null)} className="text-xs text-primary hover:underline">Vis alle</button>}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                          {Object.entries(maxiPlanningData.totals.byFraction).sort((a,b)=>b[1]-a[1]).map(([f,c]) => {
                            const typeBreakdown = {};
                            maxiPlanningData.rows.filter(r => r.fraction === f).forEach(r => {
                              Object.entries(r.sizes).forEach(([t, n]) => { typeBreakdown[t] = (typeBreakdown[t] || 0) + n; });
                            });
                            return (
                            <div key={f}
                              onClick={() => setFractionFilter(fractionFilter === f ? null : f)}
                              className={`rounded p-3 text-center cursor-pointer transition-all ${fractionFilter === f ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted/50 hover:bg-muted/80'}`}
                            >
                              <div className="text-xl font-bold">{c}</div>
                              <div className="text-sm text-muted-foreground">{f}</div>
                              <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">Skilte: {maxiPlanningData.signsByFraction?.[f] || 0}</div>
                              <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1 break-words">
                                {Object.entries(typeBreakdown).sort((a,b) => a[0].localeCompare(b[0])).map(([t, n], i) => (
                                  <span key={t}>{i > 0 ? ' · ' : ''}{t}: {n}</span>
                                ))}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {Object.keys(maxiPlanningData.totals.byType).length > 0 && (
                      <div className="bg-card border border-border rounded-lg p-4">
                        <h3 className="font-bold mb-4">Total per container-type</h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(maxiPlanningData.totals.byType).sort((a,b)=>b[1]-a[1]).map(([t,c]) => (
                            <div key={t} className="bg-muted/50 rounded p-3 text-center"><div className="text-xl font-bold">{c}</div><div className="text-sm text-muted-foreground break-words">{t}</div></div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Ingen data endnu */}
                {((planningSubTab === 'ressourceøer' && !planningData) ||
                  (planningSubTab === 'ressourcespande' && !binPlanningData) ||
                  (planningSubTab === 'maxicontainere' && !maxiPlanningData)) && (
                  <div className="text-center py-16 text-muted-foreground"><Package className="h-16 w-16 mx-auto mb-4 opacity-50" /><p>Klik "Opdater" for at hente oversigt.</p></div>
                )}

                {/* Kapacitetsoversigt */}
                {br18Data?.cards?.length > 0 && (() => {
                  const typeMap = { 'ressourceøer': 'Ressourceø', 'ressourcespande': 'Ressourcespand', 'maxicontainere': 'Maxicontainer' };
                  const filteredCards = br18Data.cards.filter(c => c.type === typeMap[planningSubTab]);
                  if (filteredCards.length === 0) return null;
                  return (
                  <div className="mt-8 border-t border-border pt-6" id="capacity-overview">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-lg">Kapacitetsoversigt</h3>
                      {!isGuest && (
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={exportBR18} data-testid="export-br18-btn">
                            <FileDown className="h-4 w-4 mr-2" />Excel
                          </Button>
                          <Button size="sm" variant="outline" onClick={exportCapacityPDF} data-testid="export-capacity-pdf-btn">
                            <FileDown className="h-4 w-4 mr-2" />PDF
                          </Button>
                          <Button size="sm" variant="outline" onClick={exportOrderListPDF} data-testid="export-order-list-pdf-btn">
                            <FileDown className="h-4 w-4 mr-2" />Bestillingsliste
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" style={{ alignItems: 'start' }}>
                      {filteredCards.map(card => (
                        <div key={card.id} className="bg-card border border-border rounded-lg p-4 flex flex-col" data-testid={`br18-card-${card.id}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-bold">{card.name}</span>
                            </div>
                          </div>
                          <div className="text-sm font-mono">
                            <table className="w-full" style={{ borderCollapse: 'collapse', tableLayout: 'auto' }}>
                              <tbody>
                            {card.lines.map((line, i) => (
                              <tr key={i}>
                                <td className="pr-2 py-0.5 align-top break-words" style={{ wordBreak: 'break-word' }}>{line.fraction}</td>
                                <td className="pr-2 py-0.5 align-top break-words" style={{ wordBreak: 'break-word' }}>{line.count}x{line.size}</td>
                                <td className="text-center whitespace-nowrap py-0.5 align-top" style={{ width: '16px' }}>=</td>
                                <td className="text-right whitespace-nowrap py-0.5 align-top" style={{ width: '70px' }}>{line.capacity > 0 ? `${line.capacity.toLocaleString('da-DK')}L` : '-'}</td>
                              </tr>
                            ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="border-t border-border mt-2 pt-2 flex justify-between font-bold text-sm">
                            <span>Total kapacitet</span>
                            <span>{card.totalLiters > 0 ? `${card.totalLiters.toLocaleString('da-DK')}L` : '-'}</span>
                          </div>
                          <div className="mt-2">
                            <textarea
                              placeholder="Notat..."
                              defaultValue={card.note}
                              rows={2}
                              onBlur={(e) => { if (e.target.value !== card.note) saveBr18Note(card, e.target.value); }}
                              className="w-full text-xs p-1.5 rounded border border-border bg-input/50 font-mono placeholder:text-muted-foreground/50 resize-y min-h-[2rem]"
                              data-testid={`br18-note-${card.id}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg text-center font-bold">
                      Samlet kapacitet: {filteredCards.reduce((a, c) => a + c.totalLiters, 0).toLocaleString('da-DK')}L
                      ({filteredCards.length} punkter)
                    </div>
                  </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </main>

      {/* Nulstil Analytics Dialog */}
      {showResetDialog && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md mx-4 shadow-xl" data-testid="reset-dialog">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-full"><AlertCircle className="h-6 w-6 text-red-500" /></div>
              <div>
                <h3 className="text-lg font-bold">Nulstil Analytics</h3>
                <p className="text-sm text-muted-foreground">Denne handling kan ikke fortrydes</p>
              </div>
            </div>
            <div className="space-y-3 mb-6">
              <p className="text-sm">Al analytics-data for dette event slettes:</p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                <li>Tømningshistorik og logs</li>
                <li>Tømningsanmodninger</li>
                <li>Scan-logs og notifikationer</li>
                <li>Container-status nulstilles til grøn</li>
              </ul>
              <div>
                <label className="text-sm font-medium block mb-1">Indtast 4-cifret PIN for at bekræfte</label>
                <input
                  type="password"
                  maxLength={4}
                  value={resetPin}
                  onChange={(e) => setResetPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="****"
                  className="flex h-10 w-full rounded-md border border-input bg-input/50 px-3 py-2 text-lg font-mono text-center tracking-[0.5em] focus:border-red-500 focus:ring-1 focus:ring-red-500"
                  data-testid="reset-pin-input"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => { setShowResetDialog(false); setResetPin(''); }} data-testid="reset-cancel-btn">Annuller</Button>
              <Button variant="destructive" className="flex-1" onClick={handleResetAnalytics} disabled={resetting || resetPin.length !== 4} data-testid="reset-confirm-btn">
                {resetting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <AlertCircle className="h-4 w-4 mr-2" />}
                {resetting ? 'Nulstiller...' : 'Nulstil alt'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
